import React, {
  ElementRef,
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from "react"
import {
  Center,
  Flex,
  Stack,
  Text,
  Box,
  CircularProgress
} from "@chakra-ui/react"
import styled from "@emotion/styled"
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil"
import {
  LOW_VIDEO_STREAM_TYPE,
  IS_SHOW_RESOLUTION_BOX,
  RENDER_STREAM_ON_CANVAS
} from "../../constants"
import { SDKContext } from "../../context/SDKContext"
import useWindowSize from "../../hooks/useWindowSize"
import layoutStates from "../../recoil/atoms/layout"
/* import { settingsDevicesSelector } from "../../recoil/atoms/settings" */
import {
  // localUserIdAtom,
  usersSelector
} from "../../recoil/atoms/users"
import { isMobileDevice } from "../../util/browser"
import { detectDeviceType, isIpad } from "../../util/deviceType"
import { UserAvatar } from "../common/avatar/"
import { LeaveOverlay } from "./leave-overlay"
import {
  watchPartyScreenLinkSelector,
  watchPartyScreenSelector
} from "../../recoil/atoms/watchPartyScreen"
import { ConsoleLogger } from "../../../src/util/logger"
import { pipParamsSelector } from "../../recoil/atoms/pictureInPicture"
import PiPBgBox from "../common/pipBgBox"
import RainbowSpinner from "../common/rainbowSpinner"
import useMainVideoStreamType from "../../hooks/useMainVideoStreamType"
import AutoPlayFailedOverlay from "./autoplay-failed-overlay"
import {
  WhiteboardRoomDetails,
  whiteboardRoomDetailsAtom,
  whiteboardShareUserAtom,
  WhiteboardShareuserEntity
} from "../../recoil/atoms/whiteboard"
import { TrulienceAvatar } from "../../conditionals/Avatar/TrulienceAvatar"
import canvasStreamController from "../../util/CanvasStreamController."
import {
  locallMuteCallbackSelector,
  localMuteAtom
} from "../../recoil/atoms/local-mute"
import { playerPropsSelector } from "../../recoil/atoms/player"
import { shareLinksModalAtom } from "../../recoil/atoms/layout/share-links-modal"
import { ShareLinksModal } from "./share-links-modal"
import MinimalWpPlayer from "./videoPlayer/minimalWpPlayer"
import { MainVideoScreen } from "./_mainVideoScreen"
import PlayerPanel from "./videoPlayer/controls-player/player-panel"
import { useIsModLive } from "src/hooks/useIsModLive"
import { FloatingControlsCtx } from "./floatingControls"
import { useCurrentAvatarInfo } from "src/conditionals/Avatar"
import usePlayerVisibility from "src/recoil/atoms/player/usePlayerVisibility"

const logger = new ConsoleLogger()
const Whiteboard = React.lazy(() => import("./whiteboard"))

interface MediaPlayerProps {
  uid: string
  index?: number
  isSmallWindow?: boolean
  styling?: React.CSSProperties
  dragging?: boolean
  showLeave?: boolean
  gridEnabled?: boolean
  setShowLeaveOverlay?: (showLeaveOverlay: boolean) => void
  showLeaveOverlay?: boolean
  showNBAOverlay?: boolean
  header?: React.ReactNode
  overlay?: React.ReactNode
  isTablet?: boolean
  isLandscape?: boolean
  isTouchDelay?: boolean
  isMobileIOS?: string
  displayProp?: boolean
  hideAvatar?: boolean
  isMainMobileScreen?: boolean
  localUserProperties?: any
  isSafariMobile?: boolean
  widthLeaveOverlay?: any
  isMobilePiPEnabled?: boolean
  heightLeaveOverlay?: string
  handleShowFullscreen?: () => void
  whiteboardShareUser?: WhiteboardShareuserEntity
  whiteboardRoomDetails?: WhiteboardRoomDetails
  isFullScreen?: boolean
}

interface StyledProps {
  ismobileios?: string
  usercamenabled?: string
}

const VideoContainer = styled(Flex)<StyledProps>`
  > div > video {
    position: static !important;
    border-radius: ${({ ismobileios }) =>
      ismobileios === "true" ? "0.375rem" : ""};
  }

  > div {
    background-color: unset !important;
    width: ${({ usercamenabled }) =>
      usercamenabled === "true" ? "100% !important" : "0% !important"};
  }
`

const MediaPlayer = React.memo(
  ({
    uid,
    index,
    styling,
    isSmallWindow,
    dragging,
    gridEnabled = false,
    showLeave = false,
    showLeaveOverlay,
    setShowLeaveOverlay,
    showNBAOverlay,
    header,
    overlay,
    isTablet,
    isLandscape,
    isTouchDelay,
    isMobileIOS,
    displayProp,
    hideAvatar,
    isMainMobileScreen,
    localUserProperties,
    isSafariMobile,
    widthLeaveOverlay,
    isMobilePiPEnabled,
    heightLeaveOverlay,
    handleShowFullscreen,
    whiteboardShareUser,
    whiteboardRoomDetails,
    isFullScreen = false
  }: MediaPlayerProps) => {
    const setHideLeaveButton = useSetRecoilState(layoutStates.hideLeaveButton)
    const { screenSize } = useWindowSize()
    const { togglePlayerVisibility } = usePlayerVisibility()
    const isMobile = isMobileDevice(screenSize) && !isTablet
    const device = detectDeviceType()
    const { coverImage, isActivePiP } = useRecoilValue(pipParamsSelector)
    const setLocalMuted = useSetRecoilState(localMuteAtom)
    const locallMuteCallback = useRecoilValue(locallMuteCallbackSelector)

    const meetingClient = React.useContext(SDKContext)
    const setPlayerProps = useSetRecoilState(playerPropsSelector)
    const userObj = useRecoilValue(usersSelector(uid?.replace("ss", "")))
    const isFloatingDivActive = useRecoilValue(layoutStates.isFloatingDivActive)
    const keepLeaveOverlayActive = useRecoilValue(
      layoutStates.keepLeaveOverlayActive
    )

    const showShareLinksModal = useRecoilValue(shareLinksModalAtom)

    /* const settingsDevices = useRecoilValue(settingsDevicesSelector) */
    const mainVideoStreamType = useMainVideoStreamType()
    const isLocalVideoTrack = uid === localUserProperties?.localUserId

    const mediaConnected = React.useMemo(() => {
      return userObj?.mediaConnected ?? false
    }, [userObj?.mediaConnected])

    /* const hideUserAvatar = React.useMemo(() => {
      return userObj?.micEnabled === true && userObj?.camEnabled !== true ? false : true
    },[userObj?.camEnabled, userObj?.micEnabled]) */

    let hideUserAvatar = true
    if (userObj?.micEnabled === true && userObj?.camEnabled !== true) {
      hideUserAvatar = false
    }

    const screenShareEnabled = userObj?.screenShareEnabled ?? false

    const showWhiteboard =
      whiteboardShareUser != null && whiteboardRoomDetails != null

    const isRtmpStreamingVideo = uid?.startsWith("rtmp")

    const [fullScreen] = useRecoilState(layoutStates.fullScreen)

    const isNonSupportingBrowser = device === "Desktop" || isIpad() === true

    useEffect(() => {
      ;(async () => {
        if (localUserProperties?.localUserId === undefined) {
          // If main window users in not a local user
          if (uid !== "" && uid !== null && uid !== undefined) {
            if (uid.substring(0, 2) !== "wp" && uid.substring(0, 2) !== "ss") {
              if (window.MeetOnline._moClient?._rtc.isConnected()) {
                if (isSmallWindow === true) {
                  logger.log(
                    "RTC Media: Setting RemoteVideoStreamType: ",
                    LOW_VIDEO_STREAM_TYPE,
                    `-- uid: ${uid}`
                  )
                  // TODO move condition up
                  await window.MeetOnline._moClient?._rtc?.client.setRemoteVideoStreamType(
                    uid,
                    LOW_VIDEO_STREAM_TYPE
                  )
                } else {
                  try {
                    logger.log(
                      "RTC Media: Setting Main RemoteVideoStreamType: ",
                      mainVideoStreamType,
                      ` -- uid: ${uid}`
                    )
                    await window.MeetOnline._moClient?._rtc?.client.setRemoteVideoStreamType(
                      uid,
                      mainVideoStreamType
                    )
                  } catch (e) {}
                }
              }
            }
          }
        }
      })()
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [uid, localUserProperties?.localUserId, mainVideoStreamType])

    let NBAOverlayText: string = ""
    if (
      localUserProperties?.camEnabled === true &&
      localUserProperties?.micEnabled === true
    ) {
      NBAOverlayText = "You can be seen and heard"
    } else if (
      localUserProperties?.camEnabled === true &&
      localUserProperties?.micEnabled === false
    ) {
      NBAOverlayText = "Your video can be seen"
    } else if (
      localUserProperties?.camEnabled === false &&
      localUserProperties?.micEnabled === true
    ) {
      NBAOverlayText = "Your audio can be heard"
    }

    let audioTrack: any
    let videoTrack: any
    if (mediaConnected === true || isRtmpStreamingVideo) {
      let mediaTracks = meetingClient?.getMediaTracks(uid)
      audioTrack = mediaTracks?.audioTrack
      videoTrack = mediaTracks?.videoTrack
    }

    // useEffect(() => {
    //   if (mediaConnected === true) {
    //     let mediaTracks = meetingClient?.getMediaTracks(uid)
    //     if (
    //       // (localUserProperties?.localUserId === uid || Object.keys(localUserProperties).length == 0) &&
    //       localUserProperties?.localUserId !== undefined &&
    //       settingsDevices.audio?.id &&
    //       settingsDevices.video?.id
    //     ) {
    //       // mediaTracks?.audioTrack?.setDevice(settingsDevices.audio?.id)
    //       // mediaTracks?.videoTrack?.setDevice(settingsDevices.video?.id)
    //       // changeCurrentDevice()
    //     }
    //     audioTrack = mediaTracks?.audioTrack
    //     videoTrack = mediaTracks?.videoTrack
    //   }
    // }, [
    //   mediaConnected,
    //   // userObj?.camEnabled,
    //   // userObj?.micEnabled
    // ])

    if (screenShareEnabled === true) {
      let mediaTracks = meetingClient?.getMediaTracks(uid)
      videoTrack = mediaTracks?.videoTrack
    }
    const container = useRef<ElementRef<"div">>(null)
    const isWatchParty = useRecoilValue(watchPartyScreenSelector)
    const wpSrc = useRecoilValue(watchPartyScreenLinkSelector)

    // const changeCurrentDevice = React.useCallback(async (): Promise<void> => {
    //   const localUserTracks = meetingClient?.getMediaTracks(uid)
    //   if (settingsDevices.audio?.id) {
    //     await localUserTracks?.audioTrack?.setDevice?.(
    //       settingsDevices.audio?.id
    //     )
    //   }
    //   if (settingsDevices.video?.id) {
    //     if(localUserTracks?.videoTrack?.enabled) {
    //       await localUserTracks?.videoTrack?.setDevice?.(
    //         settingsDevices.video?.id
    //         )
    //     }
    //   }

    //   // eslint-disable-next-line react-hooks/exhaustive-deps
    // }, [
    //   // localUserProperties?.localUserId,
    //   uid,
    //   settingsDevices.audio?.id,
    //   settingsDevices.video?.id
    //   // audioTrack,
    //   // videoTrack
    // ])

    // useEffect(() => {
    //   // set chosen devices from settings to tracks for current user
    //   if (
    //     uid &&
    //     (localUserProperties?.localUserId === uid ||
    //       // eslint-disable-next-line eqeqeq
    //       Object.keys(localUserProperties).length == 0) &&
    //     settingsDevices.audio?.id &&
    //     settingsDevices.video?.id
    //   ) {
    //     changeCurrentDevice()
    //   }
    //   // eslint-disable-next-line react-hooks/exhaustive-deps
    // }, [meetingClient])

    // const mobileWidth = isMobile && screenSize.width
    // const mobileHeight = isMobile && screenSize.height

    const renderVideoOnCanvas = useCallback(() => {
      const videoElement = container.current.getElementsByTagName("video")?.[0]
      const canvasElement =
        container.current.getElementsByTagName("canvas")?.[0]

      const noElementPresent = !videoElement && !canvasElement
      if (noElementPresent) return

      // Hide the video element and show canvas element
      videoElement.style.visibility = "hidden"
      canvasElement.style.visibility = "visible"

      // Start draw video on canvas
      canvasStreamController.drawVideo(
        videoElement,
        canvasElement,
        container.current
      )

      // Clean up function
      return () => {
        canvasStreamController.removeVideo(videoElement, canvasElement)
        canvasElement.style.visibility = "hidden"
      }
    }, [])

    useEffect(() => {
      if (!container.current) {
        return
      }
      let canvasCleanUp: Function

      if (videoTrack) {
        if (userObj?.screenShareEnabled && uid?.startsWith("ss")) {
          videoTrack?.play(container.current, { fit: "contain" })
        } else if (userObj?.camEnabled || isRtmpStreamingVideo) {
          videoTrack?.play(container.current)
        }

        if (userObj?.camEnabled && RENDER_STREAM_ON_CANVAS) {
          canvasCleanUp = renderVideoOnCanvas()
        }
      }
      return () => {
        videoTrack?.stop()
        canvasCleanUp?.()
      }
    }, [
      container,
      videoTrack,
      userObj?.camEnabled,
      userObj?.screenShareEnabled,
      isRtmpStreamingVideo,
      uid,
      mediaConnected,
      // mobileWidth,
      // mobileHeight,
      renderVideoOnCanvas
    ])

    React.useEffect(() => {
      // initial local mute state, if not initialled in 2sec because MNP video take few sec to activate
      const timerId = setTimeout(async () => {
        const isMuted = await locallMuteCallback.handleReturnData()
        if (isMuted === undefined && !isWatchParty) {
          setLocalMuted(false)
        }
      }, 2000)

      return () => clearTimeout(timerId)
    }, [])

    // useEffect(() => {
    // We play audio track while subscribing
    // if (!userObj?.muted) {
    // audioTrack?.play()
    // }
    // return () => {
    //   audioTrack?.stop()
    // }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // }, [audioTrack, mediaConnected])

    const [timeStamp, setTimeStamp] = useState({
      start: 0,
      end: 0
    })

    const [recordingView] = useRecoilState(layoutStates.recordingView)

    const setShowOnVideoOverlayContent = useSetRecoilState(
      layoutStates.showOnVideoOverlayContent
    )

    const handleClickVideo = () => {
      if (isMobile && !isTablet) {
        return
      }
      if (showLeave) {
        !dragging && setShowLeaveOverlay(!showLeaveOverlay)
      }
    }

    React.useEffect(() => {
      if (showLeaveOverlay && !keepLeaveOverlayActive) {
        let handleLeaveOverlay = setTimeout(() => {
          setShowLeaveOverlay(false)
          setShowOnVideoOverlayContent(true)
          setHideLeaveButton(false)
        }, 4000)
        return () => clearTimeout(handleLeaveOverlay)
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showLeaveOverlay, keepLeaveOverlayActive])

    const handleTouchStart = e => {
      setTimeStamp({ ...timeStamp, start: e.timeStamp })
    }

    const touchDelay = isTouchDelay ? 100 : 150

    const handleTouchEnd = React.useCallback(
      e => {
        if (e.timeStamp - timeStamp.start < touchDelay) {
          togglePlayerVisibility({ autoHide: true })
          setShowLeaveOverlay(!showLeaveOverlay)
          setPlayerProps({ isOpenControlsMobile: !showLeaveOverlay })
        }
        setTimeStamp({ start: 0, end: 0 })
      },
      [setShowLeaveOverlay, showLeaveOverlay, timeStamp.start, touchDelay]
    )

    const heightContainer = React.useMemo(() => {
      // return ((!fullScreen && isNonSupportingBrowser) || isMobile) && "100%"
      return (!fullScreen || isMobile) && "100%"
    }, [fullScreen, isMobile])

    const displayMediaPlayer = React.useMemo(() => {
      if (isMobile) {
        if (isSmallWindow) {
          return "flex"
        } else {
          if (displayProp) {
            return "flex"
          } else {
            return "flex"
          }
        }
      } else {
        return "flex"
      }
    }, [isMobile, isSmallWindow, displayProp])

    let videoResolution = "Local video"
    if (uid !== localUserProperties?.localUserId) {
      videoResolution = "0 x 0"
    }

    const avatarStyle: React.CSSProperties = React.useMemo(() => {
      return {
        width: isSmallWindow && !gridEnabled ? "2rem" : "4rem",
        height: isSmallWindow && !gridEnabled ? "2rem" : "4rem",
        position: "absolute",
        display: showNBAOverlay ? "none" : "block",
        zIndex: userObj?.camEnabled ? 0 : 1100
      }
    }, [gridEnabled, isSmallWindow, showNBAOverlay, userObj?.camEnabled])

    // Condition
    const avatarPresent = userObj?.avatar || userObj?.name
    const smallWindowCondition = isSmallWindow && !hideUserAvatar
    const nonSmallWindowCondition = !isSmallWindow && !hideAvatar

    const trulienceAvatar = useCurrentAvatarInfo()
    // console.log(trulienceAvatar)
    const showTrulienceAvatar = !!trulienceAvatar.avatarId
    // console.log(showTrulienceAvatar)

    const showUserAvatar =
      (nonSmallWindowCondition || smallWindowCondition) &&
      avatarPresent &&
      !showWhiteboard &&
      !showTrulienceAvatar

    const showSpinner =
      !(videoTrack || showUserAvatar) &&
      !isActivePiP &&
      !showWhiteboard &&
      !showTrulienceAvatar

    const modIsLive = useIsModLive()

    const setPlayerBarActive = useSetRecoilState(layoutStates.playerBarActive)

    const [isActive, setIsActive] = useState(false)

    useEffect(() => {
      setPlayerBarActive(isActive)
    }, [isActive])

    return (
      <Center
        height={heightContainer}
        id="video"
        onClick={!isSmallWindow && !showNBAOverlay ? handleClickVideo : null}
        position={isMobile && fullScreen ? "relative" : "unset"}
        pointerEvents={isSmallWindow ? "none" : "unset"}
        display={displayMediaPlayer}
        onMouseEnter={() => {
          setIsActive(true)
        }}
        onMouseLeave={() => {
          setIsActive(false)
        }}
      >
        {IS_SHOW_RESOLUTION_BOX && (
          <Box
            className="resolutionBox"
            style={{
              background: "white",
              color: "#000",
              position: "absolute",
              top: "5px",
              left: "5px",
              padding: "5px",
              fontSize: "12px",
              zIndex: 9999999,
              borderRadius: "5px"
            }}
          >
            {/* <Text
              id={`ResoTextUsername-${uid}`}
              style={{ textAlign: "center" }}
            >
              {userObj?.name}-{isSmallWindow === true ? "small" : "main"}
            </Text>
            <Text id={`ResoTextUserId-${uid}`} style={{ textAlign: "center" }}>
              {userObj?.id}
            </Text> */}
            <Text id={`videoResolution-${uid}`}>{videoResolution}</Text>
          </Box>
        )}
        {header && header}
        {overlay && overlay}
        {isMobilePiPEnabled && (
          <PiPBgBox
            styles={{
              width: "100%",
              height: "100%"
            }}
            isActiveScreen={false}
            coverImage={coverImage}
            isTouchDelay={isTouchDelay}
            isMobile={isMobile}
            handleTouchStart={handleTouchStart}
            toggleShowLeaveOverlay={handleClickVideo}
            toggleShowLeaveOverlayTouchEnd={handleTouchEnd}
          />
        )}
        {showSpinner && <RainbowSpinner zIndex="100" />}
        {showUserAvatar && !isActivePiP && (
          <UserAvatar
            avatar={userObj?.avatar}
            avatarStyle={avatarStyle}
            name={userObj?.name}
            size={isSmallWindow && !gridEnabled ? "sm" : "lg"}
            src={userObj?.avatar}
            pos="absolute"
            colorDisk={userObj?.color}
            zIndex={userObj?.camEnabled ? "base" : null}
            onClick={!isSmallWindow ? handleClickVideo : null}
            onTouchStart={
              !isSmallWindow && !isTouchDelay ? handleTouchStart : null
            }
            onTouchEnd={!isSmallWindow && !isTouchDelay ? handleTouchEnd : null}
            display={"block"}
          />
        )}
        {showNBAOverlay && (
          <Flex
            style={{
              position: "absolute",
              top: "5%",
              backgroundColor: "#00000054",
              padding: "5px 10px",
              color: "#ffffff",
              borderRadius: "5px"
            }}
            zIndex="1200 !important"
            flex={1}
            alignItems="center"
            justifyContent="center"
            position="absolute"
          >
            <Stack alignItems="center">
              <Text fontSize={"sm"}>You are currently broadcasting.</Text>
              <Text fontSize={"xs"} marginTop="0px !important">
                {NBAOverlayText}
              </Text>
            </Stack>
          </Flex>
        )}

        {showWhiteboard && handleShowFullscreen ? (
          <Suspense fallback={<CircularProgress />}>
            <Whiteboard handleShowFullscreen={handleShowFullscreen} />
          </Suspense>
        ) : // CM(ROOM_AVATAR)
        showTrulienceAvatar ? (
          <Flex
            flex={1}
            style={{ ...styling }}
            onClick={!isSmallWindow ? handleClickVideo : null}
            onTouchStart={
              !isSmallWindow && !isTouchDelay ? handleTouchStart : null
            }
            onTouchEnd={!isSmallWindow && !isTouchDelay ? handleTouchEnd : null}
            borderRadius={
              ((isMobile && !isSmallWindow && !isTablet) || isLandscape) &&
              "0px !important"
            }
            ismobileios={isMobileIOS}
          >
            <TrulienceAvatar
              key={trulienceAvatar.avatarId}
              borderRadius={"0"}
            />
            {/* {!!wpSrc && (
              <MainVideoScreen
                widthMainScreen={"100%"}
                localUserProperties={localUserProperties}
                maxWidthOverlay={0}
                overlayWidth={0}
                maxHeightMainScreen={window.innerHeight}
                isWithoutTitle={true}
                isTablet={isTablet}
                isMediumAuctionBadge={false}
                isLeaveOverlayEnable={true}
                isSmallMainScreen={isMobile || isTablet}
              />
            )} */}
          </Flex>
        ) : (
          <>
            <VideoContainer
              position="relative"
              flex={1}
              id={`player-${index}-${uid}`}
              ref={container}
              className={
                recordingView
                  ? "recording-view-player"
                  : isNonSupportingBrowser
                  ? "safari-video-player"
                  : isWatchParty && isLandscape && isSafariMobile
                  ? ""
                  : "video-player"
              }
              usercamenabled={
                userObj?.camEnabled ||
                (screenShareEnabled && !isSmallWindow) ||
                isRtmpStreamingVideo
                  ? "true"
                  : "false"
              }
              style={styling}
              onClick={!isSmallWindow ? handleClickVideo : null}
              onTouchStart={
                !isSmallWindow && !isTouchDelay ? handleTouchStart : null
              }
              onTouchEnd={
                !isSmallWindow && !isTouchDelay ? handleTouchEnd : null
              }
              borderRadius={
                ((isMobile && !isSmallWindow && !isTablet) || isLandscape) &&
                "0px !important"
              }
              ismobileios={isMobileIOS}
            >
              {RENDER_STREAM_ON_CANVAS && (
                <canvas
                  style={{
                    width: "100%",
                    height: "100%",
                    background: styling.background ?? styling.backgroundColor,
                    visibility: "hidden",
                    objectFit: "cover",
                    transform: isLocalVideoTrack ? "rotateY(180deg)" : null,
                    position: "absolute",
                    zIndex: 1000
                  }}
                />
              )}
            </VideoContainer>
            {modIsLive && isActive && (
              <Box pos="absolute" bottom={0} w="100%">
                <PlayerPanel
                  isMobile={isMobile}
                  showLeaveButton={false}
                  showDragToResizeIcon={false}
                  showLiveIcon={true}
                  showLocalSpeakerIcon={true}
                />
              </Box>
            )}
            {showShareLinksModal && isFullScreen && (
              <ShareLinksModal isTablet={true} isMobile={isMobile} />
            )}
          </>
        )}
      </Center>
    )
  }
)

export default MediaPlayer
